import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import logo from '../../assets/Images/momentum-amp-logo.svg';

const validationSteps = [
  'Connecting to NowCerts',
  'Verifying credentials',
  'Loading your profile',
  'Preparing dashboard',
];

const trustItems = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    label: 'Enterprise-grade security',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    label: 'NowCerts single sign-on',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    label: 'Role-based access control',
  },
];

const Validation: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(false);
  const [validationStep, setValidationStep] = useState(0);

  useEffect(() => {
    localStorage.clear();
    const token = searchParams.get('token');
    validateNowcertsToken(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // useEffect(() => {
  //   let step = 0;

  //   const advance = () => {
  //     step += 1;
  //     if (step < validationSteps.length) {
  //       setValidationStep(step);
  //       setTimeout(advance, 900);
  //     } else {
  //       setTimeout(() => {
  //         sessionStorage.setItem('isAuthenticated', 'true');
  //         sessionStorage.setItem('authMethod', 'nowcerts');
  //         setLoading(false);
  //         setTimeout(() => navigate('/'), 1200);
  //       }, 800);
  //     }
  //   };

  //   setTimeout(advance, 900);
  // }, [navigate]);

  const validateNowcertsToken = (token: any) => {
    setLoading(true);
    setErrorState(false);

    // Advance the visual step indicators while the API call runs
    let step = 0;
    const stepInterval = setInterval(() => {
      step += 1;
      if (step < validationSteps.length) {
        setValidationStep(step);
      } else {
        clearInterval(stepInterval);
      }
    }, 700);

    axios({
      method: 'get',
      url: `${process.env.REACT_APP_TRUCKING_API_Url}/api/account/validatessotoken?token=${token}`,
    })
      .then((response: any) => {
        clearInterval(stepInterval);
        setValidationStep(validationSteps.length - 1);

        if (!response.data?.token) {
          localStorage.clear();
          setErrorState(true);
          setLoading(false);
          return;
        }

        localStorage.setItem('igtoken', response.data.token);
        localStorage.setItem('userName', response.data.userName);
        localStorage.setItem('NowcertsAgentId', response.data.nowCertsAgentId);
        localStorage.setItem('AvatarName', response.data.avatarName);
        localStorage.setItem('NCToken', response.data.ncToken);
        localStorage.setItem('NCAgencyId', response.data.nowCertsAgencyId);
        localStorage.setItem('name', response.data.firstName + ' ' + response.data.lastName);
        localStorage.setItem('IsAdminUser', response.data.isAdminUser);
        localStorage.setItem('agentEmail', response.data.agentEmail);

        // Mark authenticated for protected route guard
        sessionStorage.setItem('isAuthenticated', 'true');
        sessionStorage.setItem('authMethod', 'nowcerts');
        sessionStorage.setItem('userEmail', response.data.agentEmail);

        setLoading(false);
        setTimeout(() => navigate('/'), 900);
      })
      .catch((error: any) => {
        clearInterval(stepInterval);
        setLoading(false);
        setErrorState(true);

        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Unknown error while validating token!',
            life: 3000,
          });
        }
      });
  };

  const progressPct = loading
    ? Math.round(((validationStep + 1) / validationSteps.length) * 85)
    : 100;

  return (
    <div className="vp-root">
      <Toast ref={toast} />

      {/* ── Left brand panel — identical structure to login ── */}
      <div className="vp-brand">
        <div className="vp-orb vp-orb--a" />
        <div className="vp-orb vp-orb--b" />
        <div className="vp-orb vp-orb--c" />

        <div className="vp-brand-inner">
          <div className="vp-brand-eyebrow">Trucking Underwriting Portal</div>

          <h2 className="vp-brand-headline">
            Secure access,<br />verified<br />instantly.
          </h2>

          <p className="vp-brand-desc">
            Your credentials are being authenticated through NowCerts' secure sign-on system.
          </p>

          <ul className="vp-trust-list">
            {trustItems.map((item, i) => (
              <motion.li
                key={i}
                className="vp-trust-item"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.45, ease: 'easeOut' }}
              >
                <span className="vp-trust-icon">{item.icon}</span>
                <span>{item.label}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Right panel — identical structure to login ── */}
      <div className="vp-right">
        <div className="vp-grid-bg" />

        <motion.div
          className="vp-card"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Logo */}
          <div className="vp-logo-wrap">
            <img src={logo} alt="Trucking Underwriting Portal" className="vp-logo" />
          </div>

          {/* Card header */}
          <div className="vp-card-header">
            <div className="vp-icon-wrap">
              {loading ? (
                <div className="vp-icon-ring">
                  <svg className="vp-spinner-svg" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="none" stroke="#1A7A3A"
                      strokeWidth="2" strokeLinecap="round"
                      strokeDasharray="31.416" strokeDashoffset="31.416"
                    >
                      <animate attributeName="stroke-dasharray" dur="2s"
                        values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite" />
                      <animate attributeName="stroke-dashoffset" dur="2s"
                        values="0;-15.708;-31.416" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>
              ) : errorState ? (
                <div className="vp-icon-state">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#dc3545" />
                    <path d="M15 9l-6 6m0-6l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              ) : (
                <div className="vp-icon-state">
                  <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#1A7A3A" />
                    <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>

            <span className="vp-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              NowCerts Authentication
            </span>

            <h1 className={`vp-title${!loading && errorState ? ' vp-title--error' : ''}`}>
              {loading ? 'Authenticating...' : errorState ? 'Access Denied' : 'Validation Complete!'}
            </h1>

            <p className="vp-subtitle">
              {loading
                ? 'Please wait while we verify your access...'
                : errorState
                ? 'You are not authorized to access this system'
                : 'Welcome to your secure underwriting dashboard'}
            </p>
          </div>

          {/* Progress bar */}
          <div className="vp-progress-track">
            <div className="vp-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          {/* Steps */}
          <div className="vp-steps">
            {validationSteps.map((step, index) => (
              <div
                key={index}
                className={[
                  'vp-step',
                  index <= validationStep ? 'vp-step--active' : 'vp-step--pending',
                  index === validationStep && loading ? 'vp-step--current' : '',
                ].filter(Boolean).join(' ')}
              >
                <div className="vp-step-icon">
                  {index < validationStep || (!loading && !errorState) ? (
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#1A7A3A" />
                      <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : index === validationStep && loading ? (
                    <svg className="vp-step-spinner" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="8" fill="none" stroke="#1A7A3A"
                        strokeWidth="2" strokeLinecap="round"
                        strokeDasharray="25.133" strokeDashoffset="25.133"
                      >
                        <animate attributeName="stroke-dasharray" dur="1.5s"
                          values="0 25.133;12.566 12.566;0 25.133" repeatCount="indefinite" />
                        <animate attributeName="stroke-dashoffset" dur="1.5s"
                          values="0;-12.566;-25.133" repeatCount="indefinite" />
                      </circle>
                    </svg>
                  ) : (
                    <div className="vp-step-dot" />
                  )}
                </div>
                <span className="vp-step-label">{step}</span>
              </div>
            ))}
          </div>

          {/* Error actions */}
          {!loading && errorState && (
            <div className="vp-error-section">
              <div className="vp-error-box">
                <h3>Authorization Failed</h3>
                <p>Your credentials could not be verified or you don't have permission to access this system.</p>
              </div>
              <div className="vp-error-actions">
                <button className="vp-btn-retry" onClick={() => window.location.reload()}>
                  Try Again
                </button>
                <button className="vp-btn-back" onClick={() => navigate('/login')}>
                  Back to Login
                </button>
              </div>
            </div>
          )}
        </motion.div>

        <p className="vp-copyright">
          &copy; {new Date().getFullYear()} Trucking Underwriting Portal &nbsp;&middot;&nbsp; All rights reserved
        </p>
      </div>
    </div>
  );
};

export default Validation;
